import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap, useGSAP, prefersReducedMotion } from '../lib/gsap';
import { ERAS, ISSUES } from '../Pages/Trump/data';

/** Home-page feature that leads into the /trump special file. */
const TrumpFeature = () => {
   const root = useRef<HTMLElement>(null);
   const titles = ISSUES.map(i => i.figure || i.title);
   const firstCount = ISSUES.filter(i => i.era === 'first').length;
   const secondCount = ISSUES.filter(i => i.era === 'second').length;

   useGSAP(
      () => {
         if (prefersReducedMotion()) return;
         const tl = gsap.timeline({ scrollTrigger: { trigger: root.current, start: 'top 75%', once: true } });
         tl.from('[data-tf-frame]', { clipPath: 'inset(12% 6% 12% 6%)', duration: 1.6, ease: 'expo.inOut' })
            .from('[data-tf-line]', { yPercent: 110, stagger: 0.1, duration: 1.2 }, '-=0.9')
            .from('[data-tf-fade]', { autoAlpha: 0, y: 16, stagger: 0.08 }, '-=0.8');

         // Giant years drift against scroll
         gsap.to('[data-tf-years]', {
            xPercent: -18,
            ease: 'none',
            scrollTrigger: { trigger: root.current, start: 'top bottom', end: 'bottom top', scrub: true },
         });
      },
      { scope: root }
   );

   return (
      <section ref={root} className="page mb-24">
         <Link
            to="/trump"
            data-tf-frame
            className="group relative block overflow-hidden rounded-sm border border-signal/25 bg-gradient-to-br from-signal/[0.12] via-ink-900/80 to-ink-950 backdrop-blur-md transition-colors duration-500 hover:border-signal/60"
         >
            {/* Oversized background years */}
            <div
               data-tf-years
               aria-hidden="true"
               className="pointer-events-none absolute -bottom-10 left-0 whitespace-nowrap font-display text-[16rem] leading-none text-white/[0.03] md:text-[22rem]"
            >
               2017 — 2021 · 2025 — 2026 · 2017 — 2021
            </div>

            {/* Faded cut-out portrait behind the term stats */}
            <img
               src={`${process.env.PUBLIC_URL}/images/trump-wave.png`}
               alt=""
               aria-hidden="true"
               loading="lazy"
               draggable={false}
               className="pointer-events-none absolute bottom-0 right-[-6%] hidden h-[115%] select-none opacity-[0.16] grayscale contrast-125 transition-[opacity,transform] duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-2 group-hover:opacity-25 md:block"
               style={{
                  maskImage: 'linear-gradient(to top, transparent 0%, black 35%)',
                  WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 35%)',
               }}
            />

            <div className="relative grid grid-cols-1 gap-10 p-6 md:p-12 lg:grid-cols-12 lg:p-16">
               <div className="lg:col-span-7">
                  <div data-tf-fade className="mb-8 flex items-center gap-3">
                     <span className="relative flex h-2 w-2">
                        <span className="absolute inset-0 animate-ping rounded-full bg-signal opacity-60" />
                        <span className="relative h-2 w-2 rounded-full bg-signal" />
                     </span>
                     <span className="eyebrow text-signal-soft">Special file · Ongoing</span>
                  </div>

                  <h2 className="font-display text-7xl leading-[0.85] tracking-tightest text-bone md:text-9xl">
                     <span className="block overflow-hidden">
                        <span data-tf-line className="block">
                           The Trump
                        </span>
                     </span>
                     <span className="block overflow-hidden">
                        <span data-tf-line className="block italic text-signal">
                           Years.
                        </span>
                     </span>
                  </h2>

                  <p data-tf-fade className="mt-8 max-w-lg text-lg leading-relaxed text-bone-muted">
                     Two presidencies, one record. Corruption cases, ICE brutality, pardons for allies, attacks on the
                     press, all documented and sourced.
                  </p>

                  <div data-tf-fade className="mt-10 inline-flex items-center gap-4">
                     <span className="button">Open the file</span>
                     <span className="eyebrow transition-colors group-hover:text-bone">{ISSUES.length} entries</span>
                  </div>
               </div>

               <div className="flex flex-col justify-end gap-6 lg:col-span-5">
                  {(['first', 'second'] as const).map(e => (
                     <div key={e} data-tf-fade className="border-t border-white/10 pt-5">
                        <div className="flex items-baseline justify-between">
                           <p className="eyebrow">{ERAS[e].label}</p>
                           <p className="font-mono text-xs text-bone-dim">
                              {e === 'first' ? firstCount : secondCount} entries
                           </p>
                        </div>
                        <p className="mt-2 font-display text-5xl text-bone md:text-6xl">{ERAS[e].span}</p>
                     </div>
                  ))}
               </div>
            </div>

            {/* Ticker of documented headlines */}
            <div
               aria-hidden="true"
               className="relative overflow-hidden border-t border-white/[0.08] bg-ink-950/50 py-4"
            >
               <div className="inline-flex animate-marquee gap-8 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.2em] text-bone-muted [animation-duration:90s] group-hover:[animation-play-state:paused]">
                  {[...titles, ...titles].map((t, i) => (
                     <span key={i} className="flex items-center gap-8">
                        {t}
                        <span className="text-signal">✶</span>
                     </span>
                  ))}
               </div>
            </div>
         </Link>
      </section>
   );
};

export default TrumpFeature;
